import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@/v2/components/DataTable";
import { PvtBadge, quality3 } from "@/v2/components/PvtBadge";
import type { BienRow } from "@/schemas/bien";
import type { PortalPublicationRow } from "@/lib/portalPublish";
import { UNIFIED_STATUSES, unifiedPublicationStatus } from "@/lib/publicationStatus";
import { formatEuro, formatShortDate } from "@/v2/lib/publicationFormat";
import { OkBadge, UnifiedStatusCell } from "./UnifiedStatusCell";

/**
 * Colonnes COMMUNES des workspaces Publication Location et Publication Vente
 * (décision Thibaud 2026-09-16 : « les 2 pages publications doivent avoir les
 * mêmes colonnes ») : Réf · Adresse (2 lignes) · Prix · Statut (unique, cf.
 * lib/publicationStatus) · Libre le · P/V/T · Date ELS · Prêt photos.
 *
 * Date ELS et Prêt photos n'existent pas encore sur la fiche vente : « — »
 * côté Sales en attendant le module (même décision).
 *
 * Largeurs FIXES (px) + `minWidth` sur la table → colonnes non rétrécies +
 * scroll horizontal, comme le Catalogue.
 */

const DASH = <span className="text-text-muted">—</span>;

export function usePublicationColumns(opts: {
  transaction: "location" | "vente";
  immowebByBien?: Record<string, PortalPublicationRow>;
}): { columns: ColumnDef<BienRow>[]; minWidth: number } {
  const { t } = useTranslation();
  const { transaction, immowebByBien } = opts;
  const vente = transaction === "vente";

  const columns = useMemo<ColumnDef<BienRow>[]>(() => {
    const rowOf = (b: BienRow) => immowebByBien?.[b.id] ?? null;
    // « Libre le » : location = date ISO ; vente = choix du déroulant + date
    // pour « À une date précise » (ou date brute héritée d'un import).
    const libre = (b: BienRow): string => {
      if (!vente) return formatShortDate(b.seraDispoPour);
      const slug = (b.seraDispoPour || "").trim();
      if (!slug) return "";
      if (slug === "date_precise") return formatShortDate(b.venteDisponibiliteDate) || t("vente.options.seraDispoPour.date_precise");
      if (/^\d{4}-\d{2}-\d{2}/.test(slug)) return formatShortDate(slug);
      return t(`vente.options.seraDispoPour.${slug}`, { defaultValue: slug });
    };
    return [
      {
        key: "ref",
        label: t("v2.publicationWs.col.ref"),
        width: 120,
        sortValue: (b) => b.referenceInterne || b.reference,
        render: (b) => <span className="font-mono text-xs">{b.referenceInterne || b.reference || "—"}</span>,
      },
      {
        key: "adresse",
        label: t("v2.publicationWs.col.adresse"),
        width: 230,
        sortValue: (b) => b.adresse || "",
        render: (b) => (
          <div className="min-w-0">
            <div className="text-text-primary truncate">{b.adresse || "—"}</div>
            <div className="text-xs text-text-muted truncate">{[b.codePostal, b.ville].filter(Boolean).join(" ")}</div>
          </div>
        ),
      },
      {
        key: "prix",
        label: vente ? t("vente.publication.col.prix") : t("v2.publicationWs.col.loyer"),
        width: 120,
        align: "center",
        sortValue: (b) => parseFloat((vente ? b.prix : b.loyerMensuel) || "0") || 0,
        render: (b) => {
          const s = formatEuro(vente ? b.prix : b.loyerMensuel);
          return s ? <span className="font-medium text-text-primary whitespace-nowrap">{s}</span> : DASH;
        },
      },
      {
        key: "statut",
        label: t("v2.publicationWs.col.statut"),
        width: 170,
        sortValue: (b) => UNIFIED_STATUSES.indexOf(unifiedPublicationStatus(b, rowOf(b))),
        render: (b) => <UnifiedStatusCell bien={b} row={rowOf(b)} />,
      },
      {
        key: "libre",
        label: t("v2.publicationWs.col.libre"),
        width: 120,
        sortValue: (b) => (vente ? b.venteDisponibiliteDate || b.seraDispoPour || "" : b.seraDispoPour || ""),
        render: (b) => libre(b) || DASH,
      },
      {
        key: "pvt",
        label: t("v2.publicationWs.col.pvt"),
        width: 110,
        align: "center",
        sortValue: (b) => `${quality3(b.photosOk)}-${quality3(b.videoOk)}-${b.textesOk ? "ok" : "ko"}`,
        render: (b) => <PvtBadge bien={b} />,
      },
      {
        key: "dateEls",
        label: t("v2.publicationWs.col.dateEls"),
        width: 100,
        sortValue: (b) => (vente ? "" : b.edlDatePrevue || ""),
        render: (b) => (vente ? DASH : formatShortDate(b.edlDatePrevue) || DASH),
      },
      {
        key: "pretPhotos",
        label: t("v2.publicationWs.col.pretPhotos"),
        width: 100,
        align: "center",
        sortValue: (b) => (!vente && b.appartPretPhotos === true ? "1" : "0"),
        render: (b) => (vente ? DASH : <OkBadge ok={b.appartPretPhotos === true} />),
      },
    ];
  }, [t, vente, immowebByBien]);

  const minWidth = columns.reduce((s, c) => s + (c.width ?? 120), 0);
  return { columns, minWidth };
}
